import React, { useContext, useEffect, useRef } from 'react';

import { MobXProviderContext, observer } from 'mobx-react';

import ReactModalDraggable from 'react-modal-resizable-draggable';

import classNames from 'classnames';

import '../../css/GenericModal.less';
import '../../css/ModalBannerDraggable.less';

import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';

import { renderButton } from '../../utils';

const GenericModal = observer((props = {}) => {
  const {
    // t,
    actionButtonOrderArray = ['cancel', 'reset', 'confirm'],
    autoFocus = true,
    bodyMsg = '',
    buttonKeyCancelLabel = '', // button will only show if string is provided
    buttonKeyConfirmLabel = 'ok',
    buttonKeyResetLabel = '', // button will only show if string is provided
    className,
    classNameForGenericModalParent,
    closeOnDimmerClick = false,
    closeOnEscape = true,
    disableConfirmButton = false,
    disableKeystroke = false,
    disableMove = false,
    disableResize = true,
    headerMsg = '',
    initHeight,
    initWidth,
    isLoading,
    isOpen,
    left,
    minHeight = 212,
    minWidth = 450,
    onClose = () => {},
    onOpen = () => {},
    onRequestClose,
    onReset = () => {},
    overlay = 'transparent', // 'dark' | 'light' | 'medium' | 'transparent' | 'clickthrough'
    top = window.innerHeight / 2.75,
    useBasicConfirmButton = false
  } = props;

  const {
    /* managers here */
  } = useContext(MobXProviderContext);

  // Create ref for the modal to make it focusable
  const modalRef = useRef(null);

  const ModalBannerDraggable = SatCoreComponent('ModalBannerDraggable');

  // Move focus into the modal while it is open, then back to whatever opened it.
  useEffect(() => {
    if (!isOpen || !autoFocus) {
      return undefined;
    }
    const activeElement = document.activeElement;
    const previouslyFocused = activeElement instanceof HTMLElement && activeElement !== document.body
      ? activeElement
      : null;
    const timeoutId = setTimeout(() => {
      modalRef.current?.focus?.();
    }, 100);
    return () => {
      clearTimeout(timeoutId);
      if (previouslyFocused?.isConnected) {
        previouslyFocused.focus({ focusVisible: true });
      }
    };
  }, [isOpen, autoFocus]);

  // TODO unused
  // useEffect(() => {
  //   (async () => {
  //     // placeholder
  //   })();
  // }, []);

  /**
   * Triggerable by certain actions such as pressing 'escape' or clicking outside of the modal
   */
  const handleRequestClose = (event) => {
    if (event?.type !== 'click' || closeOnDimmerClick) {
      onClose?.(event);
    }
  };

  const renderGenericModal = () => {
    return (
      <div className={classNames('generic-modal-parent', classNameForGenericModalParent, `overlay-${overlay}`)}>
        <ReactModalDraggable
          className={classNames('GenericModal', className)}
          closeOnDimmerClick={closeOnDimmerClick}
          closeOnEscape={closeOnEscape}
          disableKeystroke={disableKeystroke}
          disableMove={disableMove}
          disableResize={disableResize}
          initHeight={initHeight || minHeight}
          initWidth={initWidth || minWidth}
          isOpen={isOpen}
          left={left}
          minHeight={minHeight || initHeight}
          minWidth={minWidth || initWidth}
          onClose={onClose}
          onOpen={onOpen}
          onRequestClose={onRequestClose || handleRequestClose}
          top={top}>
          <div ref={modalRef}
            className='generic-modal-wrapper'
            tabIndex={0}>
            {renderGenericModalBanner()}
            {renderGenericModalBody()}
            {renderGenericModalActions()}
          </div>
        </ReactModalDraggable>
      </div>
    );
  };

  const renderGenericModalBanner = () => {
    return (
      <div className='generic-modal-banner'>
        <ModalBannerDraggable
          label={headerMsg}
          onClose={onClose} />
      </div>
    );
  };

  const renderGenericModalBody = () => {
    return (
      <div className={classNames('generic-modal-body', {
        'generic-modal-body-loading': isLoading
      })}>
        {typeof bodyMsg === 'function' ? bodyMsg() : bodyMsg}
      </div>
    );
  };

  const renderGenericModalActions = () => {
    const onCloseActionFn = (event, buttonActionObj) => {
      return onClose(event, buttonActionObj);
    };
    const isConfirmButtonLoading = typeof props.isConfirmButtonLoading === 'boolean' ? props.isConfirmButtonLoading : isLoading;

    const buttonConfigs = {
      cancel: !!buttonKeyCancelLabel && {
        additionalClassNames: 'generic-button-cancel button-basic-bg',
        basic: true,
        buttonKey: buttonKeyCancelLabel,
        disabled: isLoading,
        onClick: onClose,
        primary: true
      },
      confirm: !!buttonKeyConfirmLabel && {
        additionalClassNames: 'generic-button-confirm button-primary-bg',
        basic: useBasicConfirmButton,
        buttonKey: buttonKeyConfirmLabel,
        disabled: isLoading || disableConfirmButton,
        loading: isConfirmButtonLoading,
        onClick: onCloseActionFn,
        primary: true
      },
      reset: !!buttonKeyResetLabel && {
        additionalClassNames: 'generic-button-reset button-basic-bg',
        basic: true,
        buttonKey: buttonKeyResetLabel,
        disabled: isLoading,
        onClick: onReset,
        primary: true
      }
    };

    return (
      <div className='generic-modal-actions'>
        {actionButtonOrderArray.map((buttonType) => {
          const buttonConfig = buttonConfigs[buttonType];
          return (
            <React.Fragment key={buttonType}>
              {buttonConfig ? renderButton(buttonConfig) : null}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return renderGenericModal();
});

export default GenericModal;

SatCoreRegister('GenericModal', GenericModal);
